import { supabase } from "@/lib/supabase"
import type { Product } from "@/types"

export interface ProductWithStats extends Product {
  activeSubs: number
  graceSubs: number
  cancelledSubs: number
  monthlyRevenue: number
}

export async function getProducts(): Promise<ProductWithStats[]> {
  const { data: products, error } = await supabase
    .from("products")
    .select("*")
    .order("created_at", { ascending: true })
  if (error) throw error
  if (!products || products.length === 0) return []

  const withStats = await Promise.all(
    (products as Product[]).map(async (p) => {
      const { data: subs } = await supabase
        .from("subscriptions")
        .select("status")
        .eq("product_slug", p.slug)

      const activeSubs = subs?.filter(s => s.status === "active").length ?? 0
      const graceSubs = subs?.filter(s => s.status === "grace_period").length ?? 0
      const cancelledSubs = subs?.filter(s => s.status === "cancelled").length ?? 0
      const monthlyRevenue = activeSubs * (p.price ?? 0)

      return { ...p, activeSubs, graceSubs, cancelledSubs, monthlyRevenue }
    })
  )

  return withStats
}

export async function createProduct(product: Omit<Product, "id" | "created_at">) {
  const { data, error } = await supabase
    .from("products")
    .insert({ ...product, created_at: new Date().toISOString() })
    .select()
    .single()
  if (error) throw error
  return data as Product
}

export async function updateProduct(id: string, updates: Partial<Product>) {
  const { error } = await supabase
    .from("products")
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq("id", id)
  if (error) throw error
}

export async function seedDefaultProducts() {
  const defaults = [
    {
      name: "HPC Calendar",
      slug: "hpc",
      description: "Heliocentric Precision Calendar — Biblical & Astronomical Harmony",
      price: 6.99,
      icon: "📅",
      is_active: true,
    },
    {
      name: "Farmers Almanac",
      slug: "almanac",
      description: "Agricultural guidance aligned with HPC months and lunar cycles",
      price: 6.99,
      icon: "🌾",
      is_active: true,
    },
  ]

  for (const p of defaults) {
    const { data: existing } = await supabase
      .from("products")
      .select("id")
      .eq("slug", p.slug)
      .maybeSingle()
    if (!existing) {
      await supabase.from("products").insert({
        ...p,
        created_at: new Date().toISOString(),
      })
    }
  }
}